import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import useInView from '../hooks/useInView';
import './ContactSection.css';
import AddressLink from './AddressLink';

const encode = (data) =>
  Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&');

const ContactSection = () => {
  const { ref, isInView } = useInView();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',        // Text der Nachricht
    preferredDate: '',
    privacy: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name === 'message' ? 'subject' : name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.privacy) {
      toast.error('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Dynamischen Betreff bauen
      const dynamicSubject = `Neue Terminanfrage – ${formData.name}${
        formData.phone ? ` – ${formData.phone}` : ''
      }`;

      const payload = {
        'form-name': 'contact',              // wichtig für Netlify
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        preferredDate: formData.preferredDate,
        message: formData.subject,           // eigentlicher Nachrichtentext
        subject: dynamicSubject,             // Betreff für Netlify-E-Mail
        privacy: formData.privacy ? 'Ja' : 'Nein',
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(payload),
      });

      if (!response.ok) {
        throw new Error('Netlify-Formular konnte nicht gesendet werden.');
      }

      toast.success(
        'Vielen Dank für Ihre Anfrage! Ich melde mich so rasch wie möglich bei Ihnen.'
      );

      // Felder zurücksetzen
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        preferredDate: '',
        privacy: false,
      });
    } catch (error) {
      console.error(error);
      toast.error('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      className={`contact-section reveal-section ${isInView ? 'is-visible' : ''}`}
      ref={ref}
    >
      <div className="container">
        <div className="section-header reveal-item" style={{ '--reveal-delay': '0ms' }}>
          <h2 className="heading-2">Termin vereinbaren</h2>
          <p className="body-large section-subtitle">
            Ich freue mich auf Ihre Kontaktaufnahme
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info reveal-item" style={{ '--reveal-delay': '100ms' }}>
            <h3 className="heading-3" style={{ marginBottom: 'var(--space-lg)' }}>
              Kontaktinformationen
            </h3>

            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">
                  <MapPin size={24} />
                </div>
                <div>
                  <h4 className="body-large" style={{ fontWeight: 600, marginBottom: '4px' }}>
                    Adresse
                  </h4>
                  <AddressLink />
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={24} />
                </div>
                <div>
                  <h4 className="body-large" style={{ fontWeight: 600, marginBottom: '4px' }}>
                    Telefon
                  </h4>
                  <a href="tel:06641510494" className="body-medium contact-link">
                    0664 1510494
                  </a>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <div>
                  <h4 className="body-large" style={{ fontWeight: 600, marginBottom: '4px' }}>
                    E-Mail
                  </h4>
                  <a
                    href="mailto:office@physiotherapie-oliverbauer.com"
                    className="body-medium contact-link"
                  >
                    office@physiotherapie-oliverbauer.com
                  </a>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <Clock size={24} />
                </div>
                <div>
                  <h4 className="body-large" style={{ fontWeight: 600, marginBottom: '4px' }}>
                    Öffnungszeiten
                  </h4>
                  <p className="body-medium">
                    Samstag: 8–20 Uhr
                    <br />
                    Weitere Termine nach Vereinbarung
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-container reveal-item" style={{ '--reveal-delay': '200ms' }}>
            {/* Netlify Form */}
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              className="contact-form"
              onSubmit={handleSubmit}
            >
              {/* Pflicht: form-name Hidden Field */}
              <input type="hidden" name="form-name" value="contact" />

              {/* Honeypot-Feld gegen Spam (wird versteckt) */}
              <p hidden>
                <label>
                  Nicht ausfüllen:
                  <input name="bot-field" />
                </label>
              </p>

              {/* Hidden subject-Feld nur für Netlify (Wert kommt aus payload.subject) */}
              <input type="hidden" name="subject" />

              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  minLength={2}
                  maxLength={120}
                  className="form-input"
                  placeholder="Ihr vollständiger Name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  E-Mail-Adresse *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="ihre.email@beispiel.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Telefonnummer *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  minLength={6}
                  maxLength={40}
                  className="form-input"
                  placeholder="0664 1510494"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Anliegen *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  minLength={10}
                  maxLength={1000}
                  rows="4"
                  className="form-input"
                  placeholder="Beschreiben Sie bitte Ihr Anliegen..."
                ></textarea>
              </div>

              <div className="form-group">
                <label htmlFor="preferredDate" className="form-label">
                  Wunschtermine (optional)
                </label>
                <input
                  type="text"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  maxLength={120}
                  className="form-input"
                  placeholder="z.B. Samstag Nachmittag oder 15.03.2025"
                />
              </div>

              <div className="form-group-checkbox">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={handleChange}
                  required
                  className="form-checkbox"
                />
                <label htmlFor="privacy" className="checkbox-label">
                  Ich stimme der Verarbeitung meiner Daten gemäß Datenschutzerklärung zu. *
                </label>
              </div>

              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Wird gesendet...' : 'Anfrage senden'}
                <Send size={20} style={{ marginLeft: '8px' }} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
